import { PLATFORM_VERSION_V2 } from "constants/platform";
import { useGetEntitiesQuery } from "store/apis/group";
import { useGetUserProfileQuery } from "store/apis/profile";
import { OnboardingStepKey } from "v2/constants/onboardingStepKey";
import { useGetGroupReviewDetailsQuery } from "v2/store/onboardingEntityApi";
import { useGetOnboardingStateQuery } from "v2/store/onboardingStateApi";
import { deriveSignupStep } from "./deriveSignupStep";

const EMPTY_COMPLETED_STEPS: OnboardingStepKey[] = [];

export type SignupResumeState = {
  isSignupPending: boolean;
  isChecking: boolean;
};

export const useSignupResumeState = (): SignupResumeState => {
  const {
    data: profile,
    isSuccess: isProfileLoaded,
    isError: isProfileFailed,
  } = useGetUserProfileQuery();

  const groupId = profile?.is_group_created ? profile.company_group : "";

  const {
    data: entitiesData,
    isSuccess: isEntitiesLoaded,
    isError: isEntitiesFailed,
  } = useGetEntitiesQuery(undefined, { skip: !groupId });

  const group = entitiesData?.groups?.find((item) => item.uuid === groupId);
  const isV2Group = group?.platform_version === PLATFORM_VERSION_V2;

  const {
    data: reviewEntities,
    isSuccess: isReviewLoaded,
    isError: isReviewFailed,
  } = useGetGroupReviewDetailsQuery(
    { groupId },
    { skip: !groupId || !isV2Group }
  );

  const {
    data: recordedState,
    isSuccess: isRecordedStateLoaded,
    isError: isRecordedStateFailed,
  } = useGetOnboardingStateQuery(undefined, {
    skip: !groupId || !isV2Group,
  });

  const hasEntities = (reviewEntities?.length ?? 0) > 0;

  const isGroupVerified = Boolean(
    profile?.is_group_created && !profile.is_restricted
  );

  const { isSignupComplete } = deriveSignupStep({
    hasAccount: true,
    hasUserDetails: true,
    isMobileVerified: Boolean(profile?.is_mobile_verified),
    isGroupCreated: Boolean(groupId),
    hasEntityType: true,
    completedSteps: recordedState?.completedSteps ?? EMPTY_COMPLETED_STEPS,
    reviewEntities,
  });

  const isProfileSettled = isProfileLoaded || isProfileFailed;
  const isEntitiesSettled = !groupId || isEntitiesLoaded || isEntitiesFailed;
  const isGroupSettled =
    !isV2Group ||
    ((isReviewLoaded || isReviewFailed) &&
      (isRecordedStateLoaded || isRecordedStateFailed));

  const isChecking = !isProfileSettled || !isEntitiesSettled || !isGroupSettled;

  return {
    isSignupPending: Boolean(
      hasEntities && !isSignupComplete && !isGroupVerified
    ),
    isChecking,
  };
};
